import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { API_URL } from "../lib/api";
import { Button } from "../components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch {}
      if (++attempts < 12) timer = setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="text-center max-w-sm">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-neutral-400" />
            <h1 className="mt-6 text-2xl font-black">Confirming your payment…</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
            <h1 className="mt-6 text-2xl font-black" data-testid="payment-confirmed">You're subscribed!</h1>
            <p className="mt-3 text-sm text-neutral-500">Your new plan is active on your account.</p>
            <Link to="/dashboard/billing" className="mt-8 inline-block" data-testid="payment-success-continue">
              <Button className="rounded-full px-8">Go to billing</Button>
            </Link>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-neutral-400" />
            <h1 className="mt-6 text-2xl font-black">Still processing</h1>
            <p className="mt-3 text-sm text-neutral-500">Your payment is being confirmed. Check billing in a minute.</p>
            <Link to="/dashboard/billing" className="mt-8 inline-block">
              <Button variant="outline" className="rounded-full px-8">Go to billing</Button>
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-red-500" />
            <h1 className="mt-6 text-2xl font-black">Something went wrong</h1>
            <Link to="/dashboard/billing" className="mt-8 inline-block">
              <Button variant="outline" className="rounded-full px-8">Back to billing</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-cancel-page">
      <div className="text-center max-w-sm">
        <XCircle className="h-12 w-12 mx-auto text-neutral-300" />
        <h1 className="mt-6 text-2xl font-black">Checkout cancelled</h1>
        <p className="mt-3 text-sm text-neutral-500">No charge was made. You can pick a plan anytime.</p>
        <Link to="/dashboard/billing" className="mt-8 inline-block" data-testid="payment-cancel-back">
          <Button className="rounded-full px-8">Back to billing</Button>
        </Link>
      </div>
    </div>
  );
}
